import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nexus/ui";
import { Archive, ArrowUpRight, Building2, Copy, Crown, ExternalLink, Globe, KeyRound, Link2, Link2Off, MoreHorizontal, Settings, Shield, Users } from "lucide-react";
import { orgColor } from "@/features/identity-hub/utils/iam.utils";
import {
  ORG_ACCENT_CARD_BORDER_ACTIVE_CLASS,
  ORG_ACCENT_CARD_BORDER_CLASS,
  ORG_ACCENT_TOP_STRIPE_CLASS,
  orgAccentCardWashStyle,
  orgAccentCardWashStyleDark,
  orgAccentVar,
} from "@/features/identity-hub/utils/org-accent-surface";
import { ImagePreviewTrigger } from "./ImagePreviewTrigger";
import { ClampedDescription } from "./ClampedDescription";
import { OrgLogoMark } from "./OrgLogoMark";

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function industryLabel(type: string): string {
  if (type === "SUPER_TENANT") return "Platform";
  if (type === "API") return "API Access";
  return "Enterprise";
}

// ─── OrgCard ─────────────────────────────────────────────────────────────────

interface OrgCardProps {
  id?: string;
  name: string;
  type: string;
  memberCount: number;
  roleCount: number;
  isSuperTenant: boolean;
  isActive: boolean;
  logoUrl?: string | null;
  brandColor?: string | null;
  description?: string | null;
  retoolUserId?: string | null;
  /** When true, the card renders in a faded "archived" state with a badge. */
  isArchived?: boolean;
  onClick: () => void;
  /** When defined, surfaces a "..." menu with a "View audit log" item. */
  onOpenAuditLog?: () => void;
  /** When defined, surfaces a "Settings" item in the "..." menu. */
  onOpenSettings?: () => void;
}

export function OrgCard({ id, name, type, memberCount, roleCount, isSuperTenant, isActive, logoUrl, brandColor, description, retoolUserId, isArchived = false, onClick, onOpenAuditLog, onOpenSettings }: OrgCardProps) {
  const { t } = useTranslation();
  const accent = brandColor ?? orgColor(name);
  const trimmedDescription = description?.trim();
  const showBrandTreatment = !isSuperTenant;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      aria-label={isArchived ? t("iam.orgCard.ariaLabelArchived", { name }) : name}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      data-rowid={id}
      className={cn(
        "group relative flex min-h-[118px] w-full cursor-pointer flex-col overflow-hidden rounded-xl border text-left",
        "transition-[border-color,box-shadow,background-color] duration-300 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isSuperTenant
          ? isActive
            ? "border-amber-500/35 bg-card shadow-[0_0_0_1px_rgba(192,38,211,0.1),0_6px_24px_-10px_rgba(124,58,237,0.16)]"
            : "border-amber-500/20 bg-card hover:border-amber-500/30 hover:shadow-[0_4px_20px_-12px_rgba(192,38,211,0.12)]"
          : isActive
            ? cn("bg-card shadow-sm", ORG_ACCENT_CARD_BORDER_ACTIVE_CLASS)
            : cn("bg-card hover:shadow-sm", ORG_ACCENT_CARD_BORDER_CLASS),
        isArchived && !isActive && "border-dashed bg-muted/30 opacity-70 hover:opacity-100",
      )}
      style={showBrandTreatment ? orgAccentVar(accent) : undefined}
    >
      {showBrandTreatment && (
        <>
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 rounded-xl transition-opacity duration-300 dark:hidden",
              isActive ? "opacity-100" : "opacity-80 group-hover:opacity-95",
            )}
            style={orgAccentCardWashStyle(accent)}
          />
          <div
            aria-hidden
            className={cn(
              "pointer-events-none absolute inset-0 hidden rounded-xl transition-opacity duration-300 dark:block",
              isActive ? "opacity-100" : "opacity-85 group-hover:opacity-100",
            )}
            style={orgAccentCardWashStyleDark(accent)}
          />
        </>
      )}

      {isSuperTenant && (
        <>
          <div
            className="iam-super-tenant-card__mesh pointer-events-none absolute inset-0 rounded-xl motion-reduce:opacity-80"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl motion-reduce:hidden"
            aria-hidden
          >
            <div className="iam-super-tenant-card__shimmer absolute inset-y-0 -left-1/2 w-1/2" />
          </div>
        </>
      )}

      <span
        aria-hidden
        className={cn(
          "absolute inset-x-0 top-0 z-[1] h-[2.5px] rounded-t-xl transition-opacity duration-300",
          isSuperTenant
            ? cn(
                "iam-super-tenant-card__accent",
                isActive ? "opacity-100" : "opacity-50 group-hover:opacity-80",
              )
            : cn("bg-[var(--org-accent)]", ORG_ACCENT_TOP_STRIPE_CLASS, isActive && "opacity-100"),
        )}
      />

      {(onOpenAuditLog || onOpenSettings) ? (
        <div
          className="absolute right-1.5 top-1.5 z-10"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t("iam.shared.moreActions", "More actions")}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-all",
                  "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                  "hover:bg-muted hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "data-[state=open]:opacity-100 data-[state=open]:bg-muted",
                )}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {onOpenSettings && (
                <DropdownMenuItem className="gap-2" onClick={() => onOpenSettings()}>
                  <Settings className="h-3.5 w-3.5" />
                  {t("iam.orgCard.menu.settings", "Settings")}
                </DropdownMenuItem>
              )}
              {id && (
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => navigator.clipboard.writeText(id)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t("iam.orgCard.menu.copyId", "Copy ID")}
                </DropdownMenuItem>
              )}
              {onOpenAuditLog && (
                <DropdownMenuItem className="gap-2" onClick={() => onOpenAuditLog()}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  {t("iam.orgCard.menu.viewAuditLog", "View audit log")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <ArrowUpRight
          aria-hidden
          className={cn(
            "absolute right-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground/60",
            "opacity-0 transition-opacity duration-200 group-hover:opacity-100",
            isActive && "opacity-100 text-primary",
          )}
        />
      )}

      <div className="relative z-[1] flex min-h-0 flex-1 flex-col p-3 pr-7 pb-3">
        <div className="flex shrink-0 items-start gap-2.5">
          <ImagePreviewTrigger src={logoUrl} alt={name}>
            <OrgLogoMark
              name={name}
              logoUrl={logoUrl}
              accent={accent}
              isSuperTenant={isSuperTenant}
              size={36}
              textClassName="text-[11px]"
            />
          </ImagePreviewTrigger>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex min-w-0 items-center gap-1">
              <span className="min-w-0 truncate text-[13px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
                {name}
              </span>
              {isSuperTenant && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center justify-center rounded-md bg-amber-500/12 p-0.5 text-amber-600 dark:text-amber-400"
                      aria-label={t("iam.orgCard.superTenant.iconLabel")}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <Crown className="h-3 w-3" strokeWidth={2.25} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {t("iam.orgCard.superTenant.tooltip")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <div className="mt-1.5 flex flex-wrap items-center justify-start gap-1">
              <IndustryChip type={type} />
              <RetoolIndicator retoolUserId={retoolUserId} />
              {isArchived && (
                <span
                  className="inline-flex h-[18px] w-fit items-center justify-start gap-0.5 rounded-md bg-muted px-1.5 text-[10.5px] font-medium leading-none text-muted-foreground"
                  title={t("iam.shared.archivedBadge", "Archived")}
                >
                  <Archive className="h-2.5 w-2.5" strokeWidth={2} />
                  {t("iam.shared.archivedBadge", "Archived")}
                </span>
              )}
            </div>

            {trimmedDescription ? (
              <ClampedDescription
                text={trimmedDescription}
                maxLines={1}
                className="line-clamp-1 break-words pt-0.5 text-[10.5px] leading-snug text-muted-foreground/80"
              />
            ) : null}
          </div>
        </div>

        <div
          className={cn(
            "mt-auto flex shrink-0 items-center gap-3 border-t border-border/40 pt-2",
            isSuperTenant && "border-amber-500/12",
          )}
        >
          <Stat icon={Users} label={t("iam.orgCard.stats.members", "Members")} value={memberCount} />
          <span className="h-4 w-px shrink-0 bg-border/60" aria-hidden />
          <Stat icon={KeyRound} label={t("iam.orgCard.stats.roles", "Roles")} value={roleCount} />
        </div>
      </div>
    </div>
  );
}

// ─── RetoolIndicator ──────────────────────────────────────────────────────────

function RetoolIndicator({ retoolUserId }: { retoolUserId?: string | null }) {
  const { t } = useTranslation();
  const value = retoolUserId?.trim();
  const hasRetool = !!value;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-[18px] shrink-0 items-center justify-center rounded-md p-0.5",
            hasRetool ? "text-primary/75" : "text-muted-foreground/45",
          )}
          aria-label={hasRetool ? t("iam.orgCard.retoolIdAria", { value }) : t("iam.orgCard.noRetoolIdAria", "No Retool ID")}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {hasRetool ? (
            <Link2 className="h-2.5 w-2.5" strokeWidth={2} />
          ) : (
            <Link2Off className="h-2.5 w-2.5" strokeWidth={2} />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {hasRetool ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium text-muted-foreground">{t("iam.orgCard.retoolIdLabel", "Retool ID")}</span>
            <span className="break-all font-mono text-xs">{value}</span>
          </div>
        ) : (
          t("iam.orgCard.noRetoolId", "No Retool ID")
        )}
      </TooltipContent>
    </Tooltip>
  );
}

// ─── IndustryChip ─────────────────────────────────────────────────────────────

function IndustryChip({ type }: { type: string }) {
  const { t } = useTranslation();

  if (type === "SUPER_TENANT") {
    return (
      <TypeChip
        label={t("iam.orgCard.type.platform.label")}
        tooltip={t("iam.orgCard.type.platform.tooltip")}
        className="bg-amber-500/12 text-amber-700 dark:text-amber-400/90"
        icon={<Shield className="h-2.5 w-2.5" strokeWidth={2} />}
      />
    );
  }
  if (type === "API") {
    return (
      <TypeChip
        label={t("iam.orgCard.type.api.label")}
        tooltip={t("iam.orgCard.type.api.tooltip")}
        className="bg-muted/80 text-muted-foreground"
        icon={<Globe className="h-2.5 w-2.5" strokeWidth={2} />}
      />
    );
  }
  return (
    <TypeChip
      label={t("iam.orgCard.type.enterprise.label")}
      tooltip={t("iam.orgCard.type.enterprise.tooltip")}
      className="bg-muted/60 text-muted-foreground"
      icon={<Building2 className="h-2.5 w-2.5" strokeWidth={2} />}
    />
  );
}

function TypeChip({
  label,
  tooltip,
  className,
  icon,
}: {
  label: string;
  tooltip: string;
  className: string;
  icon: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-[18px] w-fit cursor-default items-center justify-start gap-0.5 rounded-md px-1.5 text-[10.5px] font-medium leading-none",
            className,
          )}
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <span className="inline-flex shrink-0 items-center">{icon}</span>
          <span className="truncate">{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

// ─── Stat ────────────────────────────────────────────────────────────────────

interface StatProps {
  icon: typeof Users;
  label: string;
  value: number;
}

function Stat({ icon: Icon, label, value }: StatProps) {
  return (
    <div className="flex min-w-0 flex-1 items-baseline gap-1.5">
      <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-2.5 w-2.5" strokeWidth={2} />
        {label}
      </span>
      <span className="text-base font-semibold tabular-nums leading-none text-foreground">
        {value}
      </span>
    </div>
  );
}
